import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../../services/auth.api';
import { buttonClasses } from '@/components/ui';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const verifyToken = searchParams.get('token');
    if (!verifyToken) {
      setStatus('error');
      setErrorMessage('Invalid or missing verification token');
      return;
    }

    setToken(verifyToken);
    verifyEmailToken(verifyToken);
  }, [searchParams]);

  const verifyEmailToken = async (verifyToken: string) => {
    try {
      setStatus('loading');
      await authAPI.verifyEmail(verifyToken);
      setStatus('success');

      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Email verification error:', error);
      setStatus('error');

      if (error instanceof Error) {
        if (error.message.includes('expired')) {
          setErrorMessage('Verification link has expired. Please request a new one.');
        } else if (error.message.includes('invalid')) {
          setErrorMessage('Invalid verification link.');
        } else if (error.message.includes('already verified')) {
          setErrorMessage('Email is already verified.');
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        } else {
          setErrorMessage(error.message || 'Failed to verify email');
        }
      } else {
        setErrorMessage('Failed to verify email. Please try again.');
      }
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-meradio-primary/10 to-meradio-secondary/10 dark:from-meradio-primary/5 dark:to-meradio-secondary/5 flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-meradio-primary/10 dark:bg-meradio-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <div className="animate-spin w-8 h-8 border-4 border-meradio-primary border-t-transparent rounded-full" aria-hidden="true"></div>
            </div>
            <h1 className="text-2xl font-bold text-meradio-dark dark:text-meradio-cream mb-2">
              Verifying Email
            </h1>
            <p className="text-meradio-dark-light dark:text-meradio-dark-light">
              Please wait while we verify your email address...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-meradio-primary/10 to-meradio-secondary/10 dark:from-meradio-primary/5 dark:to-meradio-secondary/5 flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-success-100 dark:bg-success-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-success-600 dark:text-success-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-meradio-dark dark:text-meradio-cream mb-2">
              Email Verified!
            </h1>
            <p className="text-meradio-dark-light dark:text-meradio-dark-light">
              Your email has been successfully verified. Redirecting you to the dashboard...
            </p>
          </div>

          <Link
            to="/dashboard"
            className={`${buttonClasses('primary')} inline-block`}
          >
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-meradio-primary/10 to-meradio-secondary/10 dark:from-meradio-primary/5 dark:to-meradio-secondary/5 flex items-center justify-center p-4">
      <div className="card max-w-md w-full text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-danger-100 dark:bg-danger-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-danger-600 dark:text-danger-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-meradio-dark dark:text-meradio-cream mb-2">
            Verification Failed
          </h1>
          <p className="text-meradio-dark-light dark:text-meradio-dark-light mb-6" role="alert">
            {errorMessage}
          </p>
        </div>

        <div className="space-y-3">
          <Link
            to="/resend-verification"
            className={`${buttonClasses('primary', 'md', true)} inline-block`}
          >
            Request New Verification Email
          </Link>

          <Link
            to="/login"
            className="text-sm text-meradio-secondary hover:text-meradio-secondary-dark dark:hover:text-meradio-secondary transition-colors block"
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
